<template>
    <div class="py-4 container-fluid">
        <div class="row">
            <div class="col-12">
                <soft-alert
                    v-if="alertTable.state"
                    :color="alertTable.color"
                    class="mb-3"
                    dismissible
                >
                    <span class="text-sm">{{ alertTable.message }}</span>
                </soft-alert>
            </div>

            <div v-if="sinAcceso" class="col-12">
                <div class="sin-acceso text-center">
                    <i class="fas fa-lock mb-3"></i>
                    <h5 class="font-weight-bolder mb-2">Sin acceso a las transacciones</h5>
                    <p class="text-sm text-secondary mb-0">
                        Solo un administrador de la plataforma puede ver esta información.
                    </p>
                </div>
            </div>

            <div v-else class="col-12">
                <CrmPageHeader
                    title="Transacciones"
                    subtitle="Negocios cerrados de todas las inmobiliarias · solo lectura"
                    icon="fas fa-handshake"
                />

                <ul class="vista-tabs mb-4">
                    <li>
                        <button type="button" :class="{ active: pestana === 'reporte' }" @click="cambiarPestana('reporte')">
                            <i class="fas fa-chart-pie me-1"></i>
                            Reporte
                        </button>
                    </li>
                    <li>
                        <button type="button" :class="{ active: pestana === 'listado' }" @click="cambiarPestana('listado')">
                            <i class="fas fa-list me-1"></i>
                            Transacciones
                        </button>
                    </li>
                </ul>

                <reporte-negocios
                    v-if="pestana === 'reporte'"
                    v-model:filtros="filtrosResumen"
                    :inmobiliarias="inmobiliarias"
                    :resumen="resumen"
                    :cargando="cargandoResumen"
                    @search="aplicarFiltrosResumen"
                    @clear="limpiarFiltrosResumen"
                    @refresh="getResumen"
                    @ver-transacciones="verTransaccionesDe"
                />

                <table-component
                    v-else
                    :headers="headers"
                    :items="negocios.data"
                    :spinner="spinner"
                    name="Transacciones"
                >
                    <template #headers>
                        <filters-negocios
                            v-model:filtros="filtros"
                            :inmobiliarias="inmobiliarias"
                            @search="applyFilters"
                            @clear="cleanFilter"
                            @refresh="getNegocios(negocios.current_page || 1)"
                        >
                            <span class="badge bg-light text-dark border px-3 py-2 ms-auto">
                                <i class="fas fa-handshake me-1 text-secondary"></i>
                                Total: {{ negocios.total || 0 }}
                            </span>
                        </filters-negocios>
                    </template>

                    <template #columnRender="{ header, value }">
                        <span v-if="header.field === 'id'" class="text-xs text-muted">#{{ value.id }}</span>

                        <span v-else-if="header.field === 'inmobiliaria'" class="d-block celda-truncada">
                            <span class="text-sm fw-semibold text-dark" :title="value.inmobiliaria?.nombre">
                                {{ value.inmobiliaria?.nombre || '—' }}
                            </span>
                            <span class="d-block text-xxs text-muted">{{ value.inmobiliaria?.email }}</span>
                        </span>

                        <span v-else-if="header.field === 'tipo'" class="d-block">
                            <span class="text-xs">{{ value.tipo_nombre }}</span>
                            <span v-if="value.modalidad === 'mls'" class="badge bg-primary ms-1">MLS</span>
                        </span>

                        <span v-else-if="header.field === 'inmueble'" class="d-block celda-truncada">
                            <template v-if="value.inmueble">
                                <span class="text-sm text-dark" :title="value.inmueble.titulo">{{ value.inmueble.titulo || `#${value.inmueble.id}` }}</span>
                                <span class="d-block text-xxs text-muted">
                                    {{ value.inmueble.codigo_ficha }}<template v-if="value.inmueble.direccion"> · {{ value.inmueble.direccion }}</template>
                                </span>
                            </template>
                            <span v-else class="text-xxs text-muted">—</span>
                        </span>

                        <span v-else-if="header.field === 'partes'" class="d-block text-xs celda-truncada">
                            <span class="d-block">
                                <span class="text-muted">Prop.:</span> {{ nombreContacto(value.propietario) }}
                            </span>
                            <span class="d-block">
                                <span class="text-muted">{{ value.cliente_cierre_rotulo || 'Cliente' }}:</span>
                                {{ nombreContacto(value.cliente_cierre) }}
                            </span>
                        </span>

                        <span v-else-if="header.field === 'valor'" class="text-xs text-nowrap">
                            {{ formatMonto(value.resumen_negocio?.valor_principal) }}
                            <span v-if="value.tipo !== 'venta' && value.resumen_negocio?.valor_principal != null" class="text-xxs text-muted">/mes</span>
                        </span>

                        <span v-else-if="header.field === 'comision'" class="text-xs text-nowrap">
                            {{ formatMonto(value.resumen_negocio?.comision_total) }}
                        </span>

                        <span v-else-if="header.field === 'fecha_cierre'" class="d-block text-xs text-nowrap">
                            {{ formatFecha(value.resumen_negocio?.fecha_cierre) }}
                            <span
                                v-if="value.resumen_negocio?.vigencia"
                                class="badge d-block mt-1"
                                :class="claseVigencia(value.resumen_negocio.vigencia)"
                            >
                                {{ formatCampo('vigencia', value.resumen_negocio.vigencia) }}
                            </span>
                        </span>

                        <span v-else-if="header.field === 'estado'" class="badge" :class="claseEstado(value.estado)">
                            {{ value.estado_nombre || value.estado }}
                        </span>

                        <span v-else-if="header.field === 'actions'">
                            <a
                                href="#"
                                class="table-action-link"
                                title="Ver detalle"
                                @click.prevent="verDetalle(value)"
                            >
                                <i class="fas fa-eye"></i>
                            </a>
                        </span>

                        <span v-else>{{ value[header.field] }}</span>
                    </template>

                    <template #pagination>
                        <div class="d-flex justify-content-center mt-3">
                            <base-pagination
                                :current-page="negocios.current_page || 1"
                                :pageCount="negocios.last_page || 1"
                                @nextPage="pageChangeHandle('next')"
                                @previousPage="pageChangeHandle('previous')"
                                @loadPage="pageChangeHandle"
                            />
                        </div>
                    </template>
                </table-component>

                <soft-modal
                    v-if="modalDetalle && detalle"
                    :modalActive="modalDetalle"
                    size="xl"
                    @close="cerrarDetalle"
                >
                    <template #header>
                        <h5 class="modal-title mb-0">
                            <i class="fas fa-handshake me-2 text-primary"></i>
                            Transacción #{{ detalle.id }}
                        </h5>
                    </template>
                    <detalle-negocio
                        :key="detalle.id"
                        :negocio="detalle"
                        :cargando="cargandoDetalle"
                        :descargando="descargando"
                        @descargar="descargarDocumento"
                    />
                </soft-modal>
            </div>
        </div>
    </div>
</template>

<script>
import tableComponent from '@/components/tableComponent.vue';
import SoftModal from '@/components/SoftModal.vue';
import SoftAlert from '@/components/SoftAlert.vue';
import CrmPageHeader from '@/components/CrmPageHeader.vue';
import BasePagination from '@/components/pagination/BasePagination.vue';
import ReporteNegocios from './components/reporteNegocios.vue';
import FiltersNegocios from './components/filtersNegocios.vue';
import DetalleNegocio from './components/detalleNegocio.vue';
import { useNegociosPlataforma } from './useNegociosPlataforma.js';
import {
    formatMonto,
    formatFecha,
    formatCampo,
    claseEstado,
    claseVigencia,
    nombreContacto,
} from './formatoNegocios.js';

export default {
    name: 'negociosPlataforma',
    components: {
        tableComponent,
        SoftModal,
        SoftAlert,
        CrmPageHeader,
        BasePagination,
        ReporteNegocios,
        FiltersNegocios,
        DetalleNegocio
    },
    setup() {
        const negocios = useNegociosPlataforma();

        negocios.iniciar();

        return {
            ...negocios,
            formatMonto,
            formatFecha,
            formatCampo,
            claseEstado,
            claseVigencia,
            nombreContacto,
        };
    }
};
</script>

<style lang="scss" scoped>
.vista-tabs {
    display: flex;
    gap: 4px;
    list-style: none;
    padding: 0;
    margin: 0;
    border-bottom: 1px solid #E2E8F0;

    button {
        border: none;
        background: none;
        padding: 10px 16px;
        font-size: 0.85rem;
        font-weight: 600;
        color: #64748B;
        border-bottom: 2px solid transparent;
        margin-bottom: -1px;

        &:hover {
            color: #1E293B;
        }

        &.active {
            color: #2563EB;
            border-bottom-color: #2563EB;
        }
    }
}

.celda-truncada {
    max-width: 220px;

    > span,
    > span > span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    > span:first-child {
        display: block;
    }
}

.sin-acceso {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 3rem 1.5rem;

    > i {
        font-size: 2rem;
        color: #cbd5e1;
    }
}

.table-action-link {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 0.375rem;
    color: #8392ab;
    transition: all 0.15s ease;
    text-decoration: none;

    &:hover {
        background-color: #f0f2f5;
        color: #344767;
    }
}

.modal-title {
    color: #344767;
    font-weight: 600;
}
</style>
